import type { ListTitlesQuery, TitleKind } from '@jaf/contracts';

const INTERNAL_PATH = /^\/(?![/\\])/;

/** Caminho da listagem de cada tipo de título. */
const TITLE_LIST_PATHS: Readonly<Record<TitleKind, string>> = {
  PAYABLE: '/payables',
  RECEIVABLE: '/receivables',
};

/** Aceita só caminhos internos do SPA como destino de retorno. */
export function safeReturnTo(value: string | null | undefined): string {
  return value !== null && value !== undefined && INTERNAL_PATH.test(value) ? value : '/';
}

function withSearch(pathname: string, params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query === '' ? pathname : `${pathname}?${query}`;
}

/** Seção de clientes/fornecedores no menu: cada papel tem a sua lista. */
export type PartySection = 'customers' | 'suppliers';

/** Helpers tipados de caminho (o React Router não tipa paths/search params sozinho). */
export const paths = {
  home: () => '/',
  login: (returnTo?: string) =>
    withSearch('/login', { returnTo: returnTo === undefined ? undefined : safeReturnTo(returnTo) }),
  forbidden: () => '/forbidden',
  /** Árvore de empresas com busca e situação (`ACTIVE`, `INACTIVE` ou `ALL`). */
  companies: (filters: { search?: string | undefined; status?: string | undefined } = {}) =>
    withSearch('/companies', { search: filters.search, status: filters.status }),
  newCompany: () => '/companies/new',
  company: (companyId: string) => `/companies/${encodeURIComponent(companyId)}`,
  /** Usuários da organização com busca, filtros, página e ordenação. */
  users: (
    filters: Partial<Record<'search' | 'status' | 'roleCode' | 'sortBy' | 'sortOrder', string>> & {
      page?: number;
    } = {},
  ) =>
    withSearch('/users', {
      search: filters.search,
      status: filters.status,
      roleCode: filters.roleCode,
      page: filters.page,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    }),
  newUser: () => '/users/new',
  /** Lista de clientes ou de fornecedores com busca, situação, página e ordenação. */
  parties: (
    section: PartySection,
    filters: Partial<Record<'search' | 'status' | 'sortBy' | 'sortOrder', string>> & {
      page?: number;
    } = {},
  ) =>
    withSearch(`/${section}`, {
      search: filters.search,
      status: filters.status,
      page: filters.page,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    }),
  newParty: (section: PartySection) => `/${section}/new`,
  importParties: (section: PartySection) => `/${section}/import`,
  party: (section: PartySection, partyId: string) => `/${section}/${encodeURIComponent(partyId)}`,
  user: (userId: string) => `/users/${encodeURIComponent(userId)}`,
  /** Organizações, para o admin geral da plataforma (ADR-0021), com busca e página. */
  platformOrganizations: (filters: { search?: string | undefined; page?: number } = {}) =>
    withSearch('/platform/organizations', { search: filters.search, page: filters.page }),
  newPlatformOrganization: () => '/platform/organizations/new',
  platformOrganization: (organizationId: string) =>
    `/platform/organizations/${encodeURIComponent(organizationId)}`,
  /** Admins gerais da plataforma. */
  platformAdministrators: () => '/platform/administrators',
  /** Tela que recebe o link do convite; o token vem no fragmento (`#token=`). */
  acceptInvitation: () => '/accept-invitation',
  /**
   * Listagem de títulos: todas as contas (`/titles`), ou só a pagar (`/payables`) ou a receber
   * (`/receivables`). Na de todas, o tipo pode vir como filtro (`kind`).
   */
  titles: (kind?: TitleKind, query: Partial<ListTitlesQuery> = {}) =>
    withSearch(kind === undefined ? '/titles' : TITLE_LIST_PATHS[kind], {
      kind: kind === undefined ? query.kind : undefined,
      page: query.page,
      pageSize: query.pageSize,
      sortBy: query.sortBy,
      sortOrder: query.sortOrder,
      status: query.status,
      search: query.search,
      dateField: query.dateField,
      from: query.from,
      to: query.to,
      ledgerAccountId: query.ledgerAccountId,
      costCenterId: query.costCenterId,
      partyId: query.partyId,
    }),
  /** Formulário de lançamento; `duplicateOf` preenche com os dados de outro título. */
  newTitle: (kind: TitleKind, duplicateOf?: string) =>
    withSearch(`${TITLE_LIST_PATHS[kind]}/new`, { duplicateOf }),
  title: (titleId: string) => `/titles/${encodeURIComponent(titleId)}`,
  /** Edição do título em aberto (TIT-7). */
  editTitle: (titleId: string) => `/titles/${encodeURIComponent(titleId)}/edit`,
  /** Cadastro de classificações (plano de contas). */
  classifications: () => '/classifications',
  costCenters: () => '/cost-centers',
  /** Contas bancárias e caixas com o saldo atual. */
  bankAccounts: () => '/bank-accounts',
  /** Fechamento de período por empresa (ADR-0025). */
  accountingPeriods: () => '/accounting-periods',
  /** IMP-1: assistente de importação de lançamentos (planilha) ou de extrato (OFX). */
  imports: (
    filters: { type?: 'titles' | 'statement'; kind?: TitleKind; bankAccountId?: string } = {},
  ) =>
    withSearch('/imports', {
      type: filters.type,
      kind: filters.kind,
      bankAccountId: filters.bankAccountId,
    }),
  /** IMP-8: conciliação das transações do extrato de uma conta. */
  bankReconciliation: (
    filters: Partial<Record<'bankAccountId' | 'status' | 'from' | 'to', string>> = {},
  ) =>
    withSearch('/bank-reconciliation', {
      bankAccountId: filters.bankAccountId,
      status: filters.status,
      from: filters.from,
      to: filters.to,
    }),
  forgotPassword: () => '/forgot-password',
  /** Tela que recebe o link do e-mail; o token vem no fragmento (`#token=`). */
  resetPassword: () => '/reset-password',
  /**
   * DRE com regime (`ACCRUAL`/`CASH`), período, mês de referência e de comparação (`AAAA-MM`)
   * e análises (`vertical=off` desliga a AV; `horizontal` liga a AH com a base).
   */
  incomeStatement: (
    filters: Partial<
      Record<
        'basis' | 'period' | 'reference' | 'compareReference' | 'vertical' | 'horizontal',
        string
      >
    > = {},
  ) =>
    withSearch('/income-statement', {
      basis: filters.basis,
      period: filters.period,
      reference: filters.reference,
      compareReference: filters.compareReference,
      vertical: filters.vertical,
      horizontal: filters.horizontal,
    }),
  /** "Meu perfil", com a troca de senha. */
  profile: () => '/account',
} as const;
